import React from 'react';

import { useGetEmployeePayoutDetailsQuery, useGetPayoutMetricAttainmentQuery } from '../../pages/metricdashboard/metricDashboardSlice';
import { AgentPayoutStatus } from '../../utils/types';
import ApprovedIcon from '../../assets/DisputeReviewedIcon.svg';
import DisputedIcon from '../../assets/DisputedIcon.svg';
import MessageInfo from '../messageInfo/MessageInfo';
import { payqualifiersFormat } from '../../features/scorecardRulesAndActivity/scorecardPayoutCriteria/scorecard-payout-criteria-service';
import { Numbers } from '../../utils/typeConstants';

const getAttainmentColor = (rank: number | null | undefined): string => {
    if (rank === 0) return '#E23636'; // Below Meets
    if (rank === 1) return '#3768E8'; // Meets Target
    if (rank === 2) return '#2AA154'; // Exceeds Target
    return '#6c757d';
};

const getAttainmentIcon = (rank: number | null | undefined): string => {
    if (rank === 0) return 'pi-times-circle';
    return 'pi-check-circle';
};

const getLegacyAttainmentColor = (attained: boolean | null | undefined): string => {
    if (attained == null) return '#6c757d';
    if (attained) return '#2AA154';
    return '#E23636';
};

const getLegacyAttainmentText = (attained: boolean | null | undefined): string => {
    if (attained == null) return 'No Data';
    if (attained) return 'Attained';
    return 'Not Attained';
};

type PayoutDetailProps = {
    payout: AgentPayoutStatus;
    scorecardroleid?: number;
};
export const PayoutDetail = ({ payout, scorecardroleid }: PayoutDetailProps) => {
    const isTierModel = payout?.scorecard_calculation_rule_id === 2;

    const { data: payoutData } = useGetEmployeePayoutDetailsQuery(
        {
            employeenumber: payout?.employee_number,
            year: payout?.year,
            month: payout?.month,
            scorecardversionid: payout?.scorecard_version_id,
            scorecardroleid,
        },
        {
            skip:
                !payout?.employee_number ||
                !payout?.scorecard_version_id ||
                !payout?.year ||
                !payout?.month ||
                !scorecardroleid,
        }
    );

    const { data: metricAttainmentData } = useGetPayoutMetricAttainmentQuery(
        {
            employeenumber: payout?.employee_number,
            year: payout?.year,
            month: payout?.month,
            scorecardversionid: payout?.scorecard_version_id,
        },
        {
            skip:
                !payout?.employee_number ||
                !payout?.scorecard_version_id ||
                !payout?.year ||
                !payout?.month,
        }
    );

    const payoutDetails = payoutData?.data ?? [];
    const qualifiers = payoutDetails?.[0]?.pay_qualifiers ?? [];
    const metricAttainments = metricAttainmentData?.data ?? [];

    if (payoutDetails?.length > 0) {
        return (
            <div className="card mt-3 border-0">
                <div className="card-header border-0" style={{ backgroundColor: '#ebf2f9', padding: '15px' }}>
                    Payout Details
                </div>
                <div className="card-body tableContentCardBody">
                    {/* ── TOP: Name / Employee Number / all employee details ─ full width ───── */}
                    <div className="row" style={{ paddingBottom: '15px', borderBottom: '1px solid #dee2e6', marginBottom: '15px' }}>
                        {[
                            {
                                label: 'Oracle ID',
                                value: payoutDetails?.[0].employee_number,
                            },

                            {
                                label: 'First Name',
                                value: payoutDetails?.[0].first_name,
                            },
                            {
                                label: 'Last Name',
                                value: payoutDetails?.[0].last_name,
                            },
                            {
                                label: 'Geo',
                                value: payoutDetails?.[0].geo_location_name,
                            },
                            {
                                label: 'Employee Type',
                                value: payoutDetails?.[0].employment_type_name,
                            },
                            {
                                label: 'Country Name',
                                value: payoutDetails?.[0].country_name,
                            },

                            {
                                label: 'HR Rate',
                                value: payoutDetails?.[0].hr_rate,
                            },
                            {
                                label: '% Payout',
                                value: (payoutDetails?.[0].payout_percentage !== undefined) ? `${(payoutDetails[0].payout_percentage * Numbers.HUNDRED)?.toFixed(Numbers.TWO)}%` : '--',
                            },
                            {
                                label: 'Registration Hours',
                                value: payoutDetails?.[0].reg_hours,
                            },
                            {
                                label: 'OT Hours',
                                value: payoutDetails?.[0].ot_hours,
                            },
                            {
                                label: 'Tenure',
                                value: payoutDetails?.[0].payout_tenure_name,
                            }
                        ].map((item) => (
                            <div className="col-md-3 padding-5" key={item.label}>
                                <div className="row">
                                    <div className="col-md-12">
                                        <strong>{item.label}</strong>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12 font-weight-100">{item.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'stretch' }}>
                        <div style={{ flex: '0 0 48%' }}>
                            <div className='col-md-12 text-italic'>Qualifiers:</div>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '160px max-content',
                                    columnGap: '10px',
                                    rowGap: '10px',
                                    alignItems: 'center',
                                    marginTop: '10px',
                                
                                }}
                            >
                                {qualifiers.length > 0 &&
                                    qualifiers.map((qualifier: any) => (
                                        <React.Fragment key={qualifier.label}>
                                            <div className="qualifier-label" style={{ marginBottom: 0 }}>{qualifier.label}</div>
                                            <div className="qualifier-value">
                                                {payqualifiersFormat(qualifier.pay_qualifier_value, qualifier.metric_type_name)}
                                            </div>
                                        </React.Fragment>
                                    ))
                                }
                                {
                                    qualifiers.length === 0 && (
                                        <div style={{ gridColumn: '1 / -1' }}>No Qualifiers</div>
                                    )
                                }

                                {/* Single Result (was duplicated before - kept the payout/qualifier-eligibility one) */}
                                <div className="qualifier-label" style={{ marginBottom: 0 }}>Result</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {payoutDetails?.[0].eligible && (
                                        <>
                                            <img src={ApprovedIcon} className='height-20' alt='Yes' />
                                            <span className='font-weight-100'>Eligible</span>
                                        </>
                                    )}
                                    {!payoutDetails?.[0].eligible && (
                                        <>
                                            <img src={DisputedIcon} className='height-20' alt='No' />
                                            <span className='font-weight-100'>Non Eligible</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ width: '1px', backgroundColor: '#dee2e6', margin: '0 60px', flexShrink: 0, alignSelf: 'stretch' }} />
                        <div style={{ flex: '1 1 auto' }}>
                            {metricAttainments.length === 0 ? (
                                <div style={{ marginTop: '10px', color: '#6c757d' }}>No Metrics</div>
                            ) : (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '160px max-content',
                                        columnGap: '10px',
                                        rowGap: '10px',
                                        alignItems: 'center',
                                        marginTop: '10px',
                                    }}
                                >
                                    {metricAttainments.map((metric: any) => (
                                        <React.Fragment key={metric.metric_label}>
                                            <div className="qualifier-label" style={{ marginBottom: 0 }}>{metric.metric_label}</div>
                                            {isTierModel ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {metric.metric_attainment_level_label && (
                                                        <i
                                                            className={`pi ${getAttainmentIcon(metric.attainment_rank)}`}
                                                            style={{
                                                                color: getAttainmentColor(metric.attainment_rank),
                                                                fontSize: '0.9rem',
                                                            }}
                                                        />
                                                    )}
                                                    <span style={{
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        color: metric.metric_attainment_level_label
                                                            ? getAttainmentColor(metric.attainment_rank)
                                                            : '#6c757d',
                                                    }}>
                                                        {metric.metric_attainment_level_label ?? 'No Data'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {metric.metric_attained != null && (
                                                        <i
                                                            className={`pi ${metric.metric_attained ? 'pi-check-circle' : 'pi-times-circle'}`}
                                                            style={{
                                                                color: metric.metric_attained ? '#2AA154' : '#E23636',
                                                                fontSize: '0.9rem',
                                                            }}
                                                        />
                                                    )}
                                                    <span style={{
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        color: getLegacyAttainmentColor(metric.metric_attained),
                                                    }}>
                                                        {getLegacyAttainmentText(metric.metric_attained)}
                                                    </span>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="col-12">
            <MessageInfo title="" message="No Payout Details found." rowHeight="50px" />
        </div>
    );
};
